import type { Dict, Mixpanel, OverridedMixpanel } from 'mixpanel-browser';
import mixpanelBrowser from 'mixpanel-browser';
import type { Prefecture, Region } from 'src/components/pages/localSouvenirs/constants/regions';
import type { TrackEventMap } from 'src/config/mixpanelEvents';
import type { RatingValue } from 'src/models/product/schema';
import { LocalStorageCache } from 'src/services/cache/LocalStorageCache';
import type { SortType } from 'src/types/productSort';

const token = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN || '';
const mixpanelEventPrefix = 'WebKaimono';

// init mixpanel
// 本番環境以外はdebugモードをtrueにする
mixpanelBrowser.init(token, process.env.NEXT_PUBLIC_WAMAZING_ENV !== 'production' ? { debug: true } : undefined);
class MixpanelWrapper implements Pick<Mixpanel, 'track'> {
  constructor(readonly mixpanel: OverridedMixpanel) {}

  // trackのラッパー関数。mixpanelEventPrefixが自動でつく
  track<EventName extends keyof TrackEventMap>(eventName: EventName, properties?: Dict): void {
    this.mixpanel.track(`${mixpanelEventPrefix}${eventName}`, {
      from:
        window.location.pathname.replace('/kaimono', '') === ''
          ? 'top'
          : window.location.pathname.replace('/kaimono', ''),
      ...properties
    });
  }

  pageView(url: string) {
    this.track('PageView', {
      url
    });
  }

  onUserSignOut() {
    this.mixpanel.reset();
  }

  // searchイベントではアプリとイベント名を揃えるためにmixpanelEventPrefixは使わない
  onSearchButtonTapped(query: string, words: string[]) {
    this.mixpanel.track('KaimonoSearchButtonTapped', {
      query,
      words
    });
  }

  onSearchBannerTapped() {
    this.mixpanel.track('KaimonoSearchBarTapped', {});
  }

  onCartChange(cartLines: { productId: number; quantity: number }[], section: string) {
    this.track('CartChange', {
      cartLines,
      section
    });
  }

  onProductCannotAddToCart(productId: number, quantity: number, reason: string) {
    this.track('ProductNotAddedToCart', {
      productId,
      quantity,
      reason
    });
  }

  onProductAddedToWishList(productIds: number[], section: string) {
    this.track('ProductAddedToWishlist', {
      productIds,
      section
    });
  }

  onProductDeletedFromWishList(productIds: number[]) {
    this.track('ProductDeletedFromWishlist', {
      productIds
    });
  }

  touchSession() {
    const now = Date.now();
    const lastActiveTimestamp = new LocalStorageCache('lastActiveTimestamp', 0);
    // 初回アクセスor前回のアクセスから30分経過している場合はSessionStartを発火する
    // 初回アクセスの場合はlastActiveTimestamp.get()が0になるので、以下の条件は常にTrueになる
    if (now - lastActiveTimestamp.get() > 1800e3) {
      this.track('SessionStart', {});
    }
    // アクセス時間を更新する
    lastActiveTimestamp.set(now);
  }

  firstSession() {
    const alreadyAccessed = new LocalStorageCache('alreadyAccessed', false);
    if (!alreadyAccessed.get()) {
      this.track('FirstSession', {});
    }
    // 一度アクセスしたらフラグをtrueにする
    alreadyAccessed.set(true);
  }

  onShopModalOpen(from: 'header' | 'cartAdd') {
    this.track('ShopModalOpen', { from });
  }

  onShopModalClose() {
    this.track('ShopModalClose', {});
  }

  onShopSelect(shopSlug: string, ignitionSource: string) {
    this.track('ShopSelect', { shopSlug, ignitionSource });
  }

  showReStockRegisterModal(productId: number) {
    this.track('ShowReStockRegisterModal', {
      productId
    });
  }

  onBackordered(productId: number, userId: string) {
    this.track('Backordered', {
      productId,
      userId
    });
  }

  onNaviRankingSectionClicked(category: string) {
    this.track('NaviRankingSectionClicked', {
      category
    });
  }

  onPopularKeywordsClicked(word: string) {
    this.track('PopularKeywordsClicked', {
      word
    });
  }

  onFeaturedContentsClicked(title: string) {
    this.track('FeaturedContentsClicked', {
      title
    });
  }

  onServiceMoreDetailClicked() {
    this.track('ServiceMoreDetailClicked', {});
  }

  onTopFeaturedCategoryClicked(featuredType: string) {
    this.track('TopFeaturedCategoryClicked', {
      featuredType
    });
  }

  onTopNavCategoryClicked(category: string, id: number) {
    this.track('TopNavCategoryClicked', {
      category,
      id
    });
  }

  onCategoryClickedInCategoryPage(category: string, slug: string) {
    this.track('CategoryClickedInCategoryPage', {
      category,
      slug
    });
  }

  onAccountRegistration() {
    this.track('AccountRegistration', {});
  }
  onAccountRegistrationBannerClicked() {
    this.track('AccountRegistrationBannerClicked', {});
  }

  onOrderCompletedButtonClicked() {
    this.track('OrderCompletedButtonClicked', {});
  }

  onFirstSurveyClicked() {
    this.track('FirstSurveyClicked', {});
  }

  onSecondSurveyClicked() {
    this.track('SecondSurveyClicked', {});
  }

  onReferralFacebookIconClicked() {
    this.track('ReferralFacebookIconClicked', {});
  }

  onReferralLineIconClicked() {
    this.track('ReferralLineIconClicked', {});
  }

  onReferralStartShoppingClicked() {
    this.track('ReferralStartShoppingClicked', {});
  }

  onYadoSimCampaignClicked() {
    this.track('YadoSimCampaignClicked', {});
  }

  onLocalSouvenirsTopAreaTapped(region: Region) {
    this.track('LocalSouvenirsTopAreaTapped', { region });
  }
  onLocalSouvenirsSpAreaTapped(region: Region) {
    this.track('LocalSouvenirsSpAreaTapped', { region });
  }
  onLocalSouvenirsSeeMoreTapped(area: string) {
    this.track('LocalSouvenirsSeeMoreTapped', { area });
  }
  onLocalSouvenirsEachPrefectureTapped(prefecture: Prefecture) {
    this.track('LocalSouvenirsEachPrefectureTapped', { prefecture });
  }
  onAddedToCartFromCampaignFancl(type: 'brightening_set' | 'porecare_set') {
    this.track('AddedToCartFromCampaignFancl', { type: type });
  }
  onCategoryInBrandClicked(brandId: number, categoryType: 'middleCategoryId' | 'smallCategoryId', categoryId: number) {
    this.track('CategoryInBrandClicked', {
      brandId,
      [categoryType]: categoryId
    });
  }
  onTopPickUpSeeMoreTapped() {
    this.track('TopPickUpSeeMoreTapped', {});
  }
  onSortBy(type: SortType, currentURL: string) {
    this.track('SortBy', {
      type,
      currentURL
    });
  }
  onQuestionnaireLinkClicked(type: 'popup' | 'sticker' | 'footer', from: string) {
    this.track('QuestionnaireLinkClicked', { type, from });
  }
  onPcBarClicked(to: string) {
    this.track('PcBarTapped', { to });
  }
  onPcSideMenuBarBannerClicked(to: string, type: 'drug' | 'souvenir' | 'popular-cosmetic-sets' | 'whatsapp') {
    this.track('PcSideMenuBarBannerClicked', { to, type });
  }
  onPcBarChangeProductQuantity(productId: number, before: number, after: number) {
    this.track('PcBarChangeProductQuantity', { productId, before, after });
  }
  onSearchAutoCompletionKeywordClicked(inputQuery: string, selectedQuery: string) {
    this.track('SearchAutoCompletionKeywordClicked', { inputQuery, selectedQuery });
  }
  onSearchHistoryDeleteClicked(history: string) {
    this.track('SearchHistoryDeleteClicked', { history });
  }
  onSearchHistoryClicked(history: string) {
    this.track('SearchHistoryKeywordClicked', { history });
  }
  onPopularKeywordClicked(keyword: string) {
    this.track('PopularKeywordClicked', { keyword });
  }
  onBrandClicked(brandId: number) {
    this.track('PopularBrandClicked', { brandId });
  }
  onBrandImageClicked(brandId: number) {
    this.track('PopularBrandImageClicked', { brandId });
  }
  onCartAdd(productId: number, quantity: number, section: string) {
    this.track('CartAdd', { productId, quantity, section });
  }
  onViewComplementaryDetail() {
    this.track('ViewComplementaryDetail');
  }
  onCheckComplementaryProduct(productId: number) {
    this.track('CheckComplementaryProduct', { productId });
  }
  onUncheckComplementaryProduct(productId: number) {
    this.track('UncheckComplementaryProduct', { productId });
  }
  onProductDetailShown(productId: number, orderable: boolean, hasBuyerComment: boolean) {
    this.track('ProductDetailShown', { productId, orderable, hasBuyerComment });
  }
  onCartRankingSectionClicked() {
    this.track('CartRankingSectionClicked');
  }
  onMakerSeeMoreTapped(brandId: number) {
    this.track('MakerSeeMoreTapped', { brandId });
  }
  onRankingProductClicked(rankingType: string, productId: number, middleCategoryId?: number, largeCategoryId?: number) {
    this.track('RankingProductClicked', { rankingType, productId, middleCategoryId, largeCategoryId });
  }
  onMediaArticleClicked(articleSlug: string) {
    this.track('MediaArticleClicked', { articleSlug });
  }
  onLocalSouvenirsAsideLeftClicked(link: string) {
    this.track('LocalSouvenirsAsideLeftClicked', { link });
  }
  onReviewRatingStarClicked(rating: RatingValue) {
    this.track('ReviewRatingStarClicked', { rating });
  }
  onReviewPostingGuidelineClicked() {
    this.track('ReviewPostingGuidelineClicked');
  }
  onReviewPostButtonClicked(productId: number, rating: RatingValue, title?: string, comment?: string, age?: string) {
    this.track('ReviewPostButtonClicked', { productId, rating, title, comment, age });
  }
  onBottomMenuTapped(type: string) {
    this.track('BottomMenuTapped', { type });
  }
  onProductTabTapped(type: 'product-information' | 'Q&A' | 'review') {
    this.track('ProductTabTapped', { type });
  }
  onClickYearEndSaleStartShoppingButton() {
    this.track('YearEndSale2024DetailShown', {});
  }
  onHamburgerCategoryClicked(category: 'large' | 'middle' | 'small', id: number) {
    this.track('HamburgerCategoryClicked', { category, id });
  }
  onHamburgerMenuClicked(
    type: 'coupon-list' | 'ranking' | 'limited_sale' | 'brands' | 'recommended' | 'featured' | 'guide' | 'mini-banner'
  ) {
    this.track('HamburgerMenuClicked', { type });
  }
}
export const mixpanel = new MixpanelWrapper(mixpanelBrowser);
